import type { Asset } from "../api/types.js";
import { basename, formatBytes, isVideoPath, pathTail } from "./asset-helpers.js";
import { AssetThumbnail } from "./asset-thumbnail.js";

// 1.D.5 Lista-naekyma asseteille. Kompakti rivirakenne — yksi rivi = yksi
// asset, sarakkeet: thumbnail (2.C.6), polku, koko, tier, metadata-status.

export type AssetListProps = {
  assets: Asset[];
  onSelect?: (asset: Asset) => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (asset: Asset, e: React.MouseEvent) => void;
};

export function AssetList({ assets, onSelect, selectedIds, onToggleSelect }: AssetListProps) {
  if (assets.length === 0) {
    return <p className="text-slate-500">Ei assettia naytettavaksi.</p>;
  }
  const selectionMode = selectedIds && selectedIds.size > 0;
  return (
    <ul className="divide-y divide-slate-800 rounded-md border border-slate-800 bg-slate-900/40">
      {assets.map((a) => {
        const isSelected = selectedIds?.has(a.id) ?? false;
        return (
          <li key={a.id}>
            <button
              type="button"
              onClick={(e) => {
                if (selectionMode || e.metaKey || e.ctrlKey || e.shiftKey) {
                  onToggleSelect?.(a, e);
                } else {
                  onSelect?.(a);
                }
              }}
              className={
                "flex w-full items-center gap-4 px-4 py-3 text-left " +
                (isSelected ? "bg-sky-950/40" : "hover:bg-slate-900")
              }
            >
              {onToggleSelect && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelect(a, e);
                  }}
                  aria-label={isSelected ? "Poista valinta" : "Valitse"}
                  className={
                    "grid h-5 w-5 flex-shrink-0 cursor-pointer place-items-center rounded border-2 text-xs " +
                    (isSelected
                      ? "border-sky-400 bg-sky-500 text-white"
                      : "border-slate-500 text-transparent hover:border-slate-300")
                  }
                >
                  ✓
                </span>
              )}
              <AssetThumbnail
                assetId={a.id}
                fallbackLabel={pathExt(a.currentPath)}
                size={256}
                alt={basename(a.currentPath)}
                isVideo={isVideoPath(a.currentPath) || Boolean(a.videoCodec)}
                className="grid h-10 w-10 flex-shrink-0 place-items-center overflow-hidden rounded bg-slate-800 text-xs font-medium text-slate-300"
              />

              <span className="min-w-0 flex-1">
                <span className="block truncate text-slate-100">{basename(a.currentPath)}</span>
                <span className="block truncate text-xs text-slate-500">
                  {pathTail(a.currentPath)}
                </span>
              </span>
              <span className="hidden w-20 text-right text-sm text-slate-400 md:inline">
                {formatBytes(a.sizeBytes ?? null)}
              </span>
              <span className="hidden w-16 text-right text-xs uppercase tracking-wide text-slate-500 md:inline">
                {a.currentTier}
              </span>
              <span className="hidden w-20 text-right text-xs text-slate-500 md:inline">
                {a.currentMetaStatus}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function pathExt(path: string): string {
  const name = basename(path);
  const dot = name.lastIndexOf(".");
  return dot >= 0
    ? name
        .slice(dot + 1)
        .toUpperCase()
        .slice(0, 4)
    : "·";
}
